// ─── batch-42 onboarding (FO-369) — blokkade-DTO + label/tekst-bron ──────────
//
// De pure resolver `bepaalBlokkade` (in @streep/utils) leidt de reden af; dit
// bestand levert de weergave: korte labels (lijst-kolom + dashboard-telling) en
// de handelingsgerichte banner-tekst met deep-link. Eén bron zodat lijst,
// dashboard en detail exact dezelfde reden tonen. `OnboardingBlokkadeReden` is
// hier gedefinieerd (types is de laag onder utils) en wordt door de resolver
// geïmporteerd.

/**
 * De afgeleide "wacht op"-reden per onboarding (spec §3.1). De volgorde van deze
 * union is de canonieke keten-prioriteit (zie `bepaalBlokkade` in @streep/utils).
 */
export type OnboardingBlokkadeReden =
  | 'GEEN'
  | 'CONCEPT'
  | 'WACHT_OP_MEDEWERKER'
  | 'CONTRACT_VELDEN_ONTBREKEN'
  | 'AANVULLEN_VEREIST'
  | 'WACHT_OP_CONTROLE'
  // Alle stappen goedgekeurd + contractvelden compleet: HR moet het contract nu
  // genereren (de deferred/velden-ronde is klaar).
  | 'CONTRACT_GENEREREN'
  | 'WACHT_OP_HANDTEKENING'
  | 'ASSETS_TOEWIJZEN'
  | 'ACTIVEREN'

/** Alle reden-keys in keten-volgorde (dashboard-telling itereert hierover). */
export const ONBOARDING_BLOKKADE_REDENEN = [
  'GEEN',
  'CONCEPT',
  'WACHT_OP_MEDEWERKER',
  'CONTRACT_VELDEN_ONTBREKEN',
  'AANVULLEN_VEREIST',
  'WACHT_OP_CONTROLE',
  'CONTRACT_GENEREREN',
  'WACHT_OP_HANDTEKENING',
  'ASSETS_TOEWIJZEN',
  'ACTIVEREN',
] as const satisfies readonly OnboardingBlokkadeReden[]

/** Alleen de blokkerende redenen (zonder GEEN) — voor de dashboard-telling. */
export const ONBOARDING_BLOKKADE_REDENEN_ACTIEF: readonly OnboardingBlokkadeReden[] =
  ONBOARDING_BLOKKADE_REDENEN.filter((r) => r !== 'GEEN')

export const ONBOARDING_BLOKKADE_REDEN_LABELS: Record<OnboardingBlokkadeReden, string> = {
  GEEN:                      'Geen blokkade',
  CONCEPT:                   'Basisgegevens afronden',
  WACHT_OP_MEDEWERKER:       'Wacht op medewerker',
  CONTRACT_VELDEN_ONTBREKEN: 'Contractvelden ontbreken',
  AANVULLEN_VEREIST:         'Aanvulling gevraagd',
  WACHT_OP_CONTROLE:         'Wacht op HR-controle',
  CONTRACT_GENEREREN:        'Contract genereren',
  WACHT_OP_HANDTEKENING:     'Wacht op handtekening',
  ASSETS_TOEWIJZEN:          'Assets toewijzen',
  ACTIVEREN:                 'Klaar - activeren',
}

/** Contract-veld-key → kort NL-label voor de handelingsgerichte tekst. */
export const ONBOARDING_CONTRACT_VELD_LABELS: Record<string, string> = {
  contractType:                 'contracttype',
  startDate:                    'startdatum',
  contractHoursPerWeek:         'contracturen',
  grossMonthlySalary:           'salaris',
  caoSchaal:                    'CAO-schaal',
  caoTrede:                     'CAO-trede',
  employee:                     'medewerkergegevens',
  ARBEIDSOVEREENKOMST_template: 'contracttemplate',
  generation_error:             'contractgeneratie',
}

/**
 * Contract-veld-key → de medewerker-tab waar HR dat veld invult. Contract- en
 * salarisgegevens zijn read-only op het overzicht (ze lopen via mutaties), dus
 * een kale link naar de medewerker laat HR zoeken naar een veld dat daar niet
 * te bewerken is. Velden zonder eigen tab (startdatum) staan op het overzicht.
 */
const ONBOARDING_CONTRACT_VELD_TAB: Record<string, 'contract' | 'salaris'> = {
  contractType:         'contract',
  contractHoursPerWeek: 'contract',
  grossMonthlySalary:   'salaris',
  caoSchaal:            'salaris',
  caoTrede:             'salaris',
}

/** Contract-veld-key → waar het veld staat, voor de uitleg in de detail-banner. */
export const ONBOARDING_CONTRACT_VELD_PLEK: Record<string, string> = {
  contractType:         'tab Contract, knop "Nieuwe mutatie"',
  contractHoursPerWeek: 'tab Contract, knop "Nieuwe mutatie"',
  startDate:            'tab Overzicht, knop "Bewerken"',
  grossMonthlySalary:   'tab Salaris, knop "Nieuwe mutatie"',
  caoSchaal:            'tab Salaris, knop "Nieuwe mutatie"',
  caoTrede:             'tab Salaris, knop "Nieuwe mutatie"',
}

/**
 * Deep-link naar de medewerker-tab waar het eerste ontbrekende contractveld
 * ingevuld wordt. Contract gaat vóór salaris: de salarismutatie rekent het
 * uurloon uit de contracturen. Dé bron voor de lijst-/detail-knop én de
 * HR-postvak-link, zodat ze nooit naar verschillende plekken wijzen.
 */
export function onboardingContractVeldenHref(
  employeeId: string,
  ontbrekendeVelden: readonly string[],
): string {
  const tabs = ontbrekendeVelden.map((v) => ONBOARDING_CONTRACT_VELD_TAB[v])
  const tab = tabs.includes('contract') ? 'contract' : tabs.includes('salaris') ? 'salaris' : null
  return tab ? `/medewerkers/${employeeId}?tab=${tab}` : `/medewerkers/${employeeId}`
}

export interface OnboardingBlokkadeSubDTO {
  reden: OnboardingBlokkadeReden
  label: string
}

/**
 * Gedeeld weergave-DTO voor de blokkade: lijst-kolom, dashboard-telling en
 * detail-banner tonen allemaal dit object (spec §3.1).
 */
export interface OnboardingBlokkadeDTO {
  reden: OnboardingBlokkadeReden
  /** true wanneer de onboarding ergens op wacht (reden !== 'GEEN'). */
  geblokkeerd: boolean
  /** Korte reden-label voor de lijst-kolom + dashboard-groepering. */
  redenLabel: string
  /** Handelingsgerichte tekst voor de detail-banner ("Vul salaris en contracturen in bij Jan"). */
  actieTekst: string
  /** Deep-link voor de directe actie-knop (relatief backoffice-pad), of null. */
  actieHref: string | null
  /** Knop-label ("Velden invullen", "Naar onboarding", ...), of null. */
  actieLabel: string | null
  /** Ontbrekende contract-veld-keys (alleen bij CONTRACT_VELDEN_ONTBREKEN). */
  ontbrekendeVelden: string[]
  /** Dagen sinds de blokkerende toestand begon; null = onbekend. */
  sindsDagen: number | null
  /** Overige actieve blokkades (detail-sublijst). */
  sublijst: OnboardingBlokkadeSubDTO[]
}

export interface OnboardingBlokkadeBuildInput {
  reden: OnboardingBlokkadeReden
  geblokkeerd: boolean
  ontbrekendeVelden: string[]
  sindsDagen: number | null
  sublijst: OnboardingBlokkadeReden[]
  voornaam: string
  employeeId: string
  onboardingId: string
}

/** "salaris en contracturen" / "startdatum, salaris en contracturen". */
function ontbrekendeVeldenTekst(velden: readonly string[]): string {
  const labels = velden.map((v) => ONBOARDING_CONTRACT_VELD_LABELS[v] ?? v)
  if (labels.length === 0) return 'de contractgegevens'
  if (labels.length === 1) return labels[0]!
  return `${labels.slice(0, -1).join(', ')} en ${labels[labels.length - 1]}`
}

/**
 * Bouw het weergave-DTO uit de pure `bepaalBlokkade`-uitkomst + context (naam +
 * ids voor de deep-links). Server-side gebruikt door de list-/detail-/dashboard-
 * routes. Kopie is STREEP-stijl, handelingsgericht, geen em-dash.
 */
export function buildOnboardingBlokkadeDTO(
  input: OnboardingBlokkadeBuildInput,
): OnboardingBlokkadeDTO {
  const naam = input.voornaam.trim() || 'de medewerker'
  const medewerkerHref = onboardingContractVeldenHref(input.employeeId, input.ontbrekendeVelden)
  const onboardingHref = `/onboarding/${input.onboardingId}`

  let actieTekst = ''
  let actieHref: string | null = null
  let actieLabel: string | null = null

  switch (input.reden) {
    case 'CONCEPT':
      actieTekst = `Rond de basisgegevens af en verstuur de welkom-link aan ${naam}.`
      actieHref = onboardingHref
      actieLabel = 'Naar onboarding'
      break
    case 'WACHT_OP_MEDEWERKER':
      actieTekst = `Wacht op de vragenlijst van ${naam}. Verstuur eventueel de welkom-link opnieuw.`
      actieHref = onboardingHref
      actieLabel = 'Naar onboarding'
      break
    case 'CONTRACT_VELDEN_ONTBREKEN':
      actieTekst = `Vul ${ontbrekendeVeldenTekst(input.ontbrekendeVelden)} in bij ${naam}, dan wordt het contract gegenereerd.`
      actieHref = medewerkerHref
      actieLabel = 'Velden invullen'
      break
    case 'AANVULLEN_VEREIST':
      actieTekst = `Verwerk de gevraagde aanvulling voor ${naam} en markeer die als aangevuld.`
      actieHref = onboardingHref
      actieLabel = 'Naar controle'
      break
    case 'WACHT_OP_CONTROLE':
      actieTekst = `Controleer en keur de ingediende stappen van ${naam}.`
      actieHref = onboardingHref
      actieLabel = 'Naar controle'
      break
    case 'CONTRACT_GENEREREN':
      actieTekst = `Alle stappen zijn goedgekeurd en de contractgegevens zijn compleet. Genereer nu het contract voor ${naam}.`
      // De genereer-actie is een POST-knop op het onboarding-detail, geen link.
      actieHref = onboardingHref
      actieLabel = 'Contract genereren'
      break
    case 'WACHT_OP_HANDTEKENING': {
      const sinds =
        input.sindsDagen !== null
          ? ` (sinds ${input.sindsDagen} ${input.sindsDagen === 1 ? 'dag' : 'dagen'})`
          : ''
      actieTekst = `Wacht op ondertekening van de arbeidsovereenkomst door ${naam}${sinds}.`
      actieHref = onboardingHref
      actieLabel = 'Bekijk documenten'
      break
    }
    case 'ASSETS_TOEWIJZEN':
      // FO-414 — noem de vervolgstap: assets zijn niet-blokkerend (spec 25 §2.8),
      // HR zet de onboarding daarna zelf op gereed voor start.
      actieTekst = `Wijs de assets en IT-toegang toe voor ${naam} en zet de onboarding daarna op gereed voor start.`
      actieHref = onboardingHref
      actieLabel = 'Naar assets'
      break
    case 'ACTIVEREN':
      actieTekst = `Alles staat klaar. Activeer ${naam} zodra de indiensttreding een feit is.`
      actieHref = onboardingHref
      actieLabel = 'Activeren'
      break
    case 'GEEN':
    default:
      break
  }

  return {
    reden: input.reden,
    geblokkeerd: input.geblokkeerd,
    redenLabel: ONBOARDING_BLOKKADE_REDEN_LABELS[input.reden],
    actieTekst,
    actieHref,
    actieLabel,
    ontbrekendeVelden: input.ontbrekendeVelden,
    sindsDagen: input.sindsDagen,
    sublijst: input.sublijst.map((r) => ({ reden: r, label: ONBOARDING_BLOKKADE_REDEN_LABELS[r] })),
  }
}
